import dayjs from 'dayjs';
import type Highcharts from 'highcharts';
import { isInteger } from 'lodash';
import type { FC } from 'react';
import { useGetLineChartDefaultOptions } from 'src/components/highcharts';
import { useColourPalette } from 'src/hooks/useColourPalette';

import { HighchartsWidget } from '../../HighchartsWidget';
import { NoWidgetData } from '../NoWidgetData';
import { WidgetLoading } from '../widget-loading';
import { getReferenceAwareYExtremes } from './referenceLineAxis';

export type Props = {
  loading?: boolean;
  xTitle: string;
  yTitle: string;
  values?: { x: Date; y: number }[];
  series?: {
    name: string;
    data: { x: string; y: number; [key: string]: unknown }[];
  }[];
  tooltipYValueFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string;
  pointColorFormatter?: (
    y: number | undefined,
    seriesIndex: number,
    custom?: Record<string, unknown>
  ) => string | undefined;
  /**
   * Flat horizontal reference lines (e.g. indicator tolerances/appetites) drawn
   * as Highcharts `yAxis.plotLines`. When present, the y-axis is extended so the
   * lines stay in view.
   */
  referenceLines?: { value: number; color: string }[];
};

const LineChartWidget: FC<Props> = ({
  values,
  series,
  loading,
  xTitle,
  yTitle,
  tooltipYValueFormatter,
  pointColorFormatter,
  referenceLines,
}) => {
  const { colours } = useColourPalette();
  const defaultOptions = useGetLineChartDefaultOptions({});

  if (loading) {
    return <WidgetLoading />;
  }

  if ((!values || values.length === 0) && (!series || series.length === 0)) {
    return <NoWidgetData />;
  }

  const dataYValues = values
    ? values.map((v) => v.y)
    : (series ?? []).flatMap((s) => s.data.map((d) => d.y));

  const yExtremes = referenceLines?.length
    ? getReferenceAwareYExtremes(
        dataYValues,
        referenceLines.map((line) => line.value)
      )
    : undefined;

  const yAxis: Highcharts.YAxisOptions = {
    allowDecimals: false,
    title: {
      text: yTitle,
    },
    labels: {
      formatter: function formatter() {
        return isInteger(this.value) ? this.value.toString() : '';
      },
    },
    ...(yExtremes ? { min: yExtremes.min, max: yExtremes.max } : {}),
    plotLines: referenceLines?.map((line) => ({
      value: line.value,
      color: line.color,
      width: 2,
      dashStyle: 'Dash',
    })),
  };

  const options: Highcharts.Options = {
    ...defaultOptions,
    chart: {
      ...defaultOptions.chart,
      events: pointColorFormatter
        ? {
            redraw: function () {
              this.series.forEach((s, index) => {
                s.points.forEach((point) => {
                  const color = pointColorFormatter(
                    point.y,
                    index,
                    point.options?.custom
                  );
                  if (color) {
                    point.update(
                      {
                        color,
                      },
                      false
                    );
                  }
                });
              });
            },
          }
        : {},
    },
    legend: {
      enabled: series ? series.length > 0 : false,
    },
    tooltip: {
      formatter: function () {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Highcharts types `this` as Point but at runtime it's a tooltip context object
        const point = (this as any).point ?? this;
        const yValue = tooltipYValueFormatter
          ? tooltipYValueFormatter(
              this.y,
              this.series.index,
              point.options?.custom
            )
          : this.y;

        // When using categories, this.key holds the formatted category string;
        // for datetime axes, this.key is the timestamp — format it with dayjs
        const xLabel =
          typeof this.key === 'string'
            ? this.key
            : dayjs(this.x).format('D/M/YY');

        return `<b>${xLabel}</b><br/>${this.series.name}: ${yValue}`;
      },
    },
    xAxis: {
      categories: values
        ? values.map((v) => dayjs(v.x).format('D/M/YY'))
        : undefined,
      title: {
        text: xTitle,
      },
      ...(!values
        ? {
            type: 'datetime',
            minTickInterval: 24 * 3600 * 1000, // one day
            labels: {
              formatter: function () {
                return dayjs(this.value).format('D/M/YY');
              },
              rotation: -45,
            },
          }
        : {}),
    },
    yAxis,
    series: values
      ? [
          {
            type: 'line',
            name: yTitle,
            data: values.map((v) => v.y),
            color: colours[0],
          },
        ]
      : series?.map((s, index) => ({
          type: 'line',
          name: s.name,
          data: s.data.map((d) => {
            const { x, y, ...custom } = d;

            return { x: new Date(x).getTime(), y, custom };
          }),
          color: colours[index % colours.length],
        })),
  };

  return <HighchartsWidget options={options} />;
};

export default LineChartWidget;
